import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ReactSVG } from "react-svg";

import arrowIcon from "../../assets/svg/arrow.svg";
import {
   getPendingInstructorRatings,
   submitInstructorRating,
} from "../../api/instructorRatingsService";
import { closePopup } from "../Common/popupUIStore";
import StudentRatingPicker from "../Common/StudentRatingPicker";
import { formatFloatingDateTime } from "../../utils/studentCalendarUtils";

import { hasActiveReservationSubstitution } from "../../utils/instructorReservations";

function getLessonStartTime(item) {
   return (
      item?.startTime ??
      item?.reservation?.startTime ??
      item?.dateTime ??
      item?.reservation?.dateTime ??
      null
   );
}

function getPersonDisplayName(person) {
   if (!person || typeof person !== "object") return "";
   const first = String(person.firstName || person.first_name || "").trim();
   const last = String(person.lastName || person.last_name || "").trim();
   const full = `${first} ${last}`.trim();
   return full || String(person.name || person.fullName || "").trim();
}

function getInstructorName(item) {
   if (!item) return "Instructor";

   if (hasActiveReservationSubstitution(item)) {
      const substitution =
         item?.substitution ??
         item?.reservation?.substitution ??
         item?.instructorSubstitution ??
         item?.reservation?.instructorSubstitution ??
         null;
      const substitute =
         item?.substituteInstructor ??
         item?.substitute_instructor ??
         substitution?.substituteInstructor ??
         substitution?.substitute_instructor ??
         item?.effectiveInstructor ??
         item?.effective_instructor ??
         item?.reservation?.effectiveInstructor ??
         item?.reservation?.effective_instructor ??
         null;
      const substituteName = getPersonDisplayName(substitute);
      if (substituteName) return substituteName;
   }

   const instructor = item?.instructor ?? item?.reservation?.instructor ?? {};
   return getPersonDisplayName(instructor) || "Instructor";
}

function getReservationId(item) {
   const id = item?.id ?? item?.reservationId;
   const parsed = Number(id);
   return Number.isFinite(parsed) ? parsed : null;
}

export default function StudentMandatoryRatingsPopup() {
   const [pending, setPending] = useState([]);
   const [loading, setLoading] = useState(true);
   const [rating, setRating] = useState(0);
   const [hoverRating, setHoverRating] = useState(0);
   const [comment, setComment] = useState("");
   const [saving, setSaving] = useState(false);
   const [error, setError] = useState("");

   const refreshPending = useCallback(async () => {
      try {
         const rows = await getPendingInstructorRatings();
         const list = Array.isArray(rows) ? rows : [];
         setPending(list);
         if (!list.length) closePopup();
         return list;
      } catch {
         setPending([]);
         closePopup();
         return [];
      } finally {
         setLoading(false);
      }
   }, []);

   useEffect(() => {
      refreshPending();
   }, [refreshPending]);

   const currentLesson = pending[0] || null;
   const totalCount = pending.length;
   const currentIndex = totalCount > 0 ? 1 : 0;
   const reservationId = useMemo(
      () => getReservationId(currentLesson),
      [currentLesson],
   );

   useEffect(() => {
      setRating(0);
      setHoverRating(0);
      setComment("");
      setError("");
   }, [reservationId]);

   const handleSubmit = async (event) => {
      event.preventDefault();
      if (!reservationId || saving) return;

      if (!rating) {
         setError("Selectează un rating de la 1 la 5 stele.");
         return;
      }

      const requiresComment = rating >= 1 && rating <= 3;
      if (requiresComment && !String(comment || "").trim()) {
         setError(
            "Pentru o evaluare mai mică de 4 stele, te rugăm să adaugi un comentariu.",
         );
         return;
      }

      setSaving(true);
      setError("");

      try {
         await submitInstructorRating({
            reservationId,
            rating,
            comment,
         });
         setPending((current) =>
            current.filter(
               (item) =>
                  Number(item?.id ?? item?.reservationId) !== reservationId,
            ),
         );
         await refreshPending();
      } catch (submitError) {
         setError(
            submitError?.message ||
               "Nu am putut trimite ratingul. Încearcă din nou.",
         );
      } finally {
         setSaving(false);
      }
   };

   return (
      <div className="studentMandatoryRatingsPopup">
         <header className="studentMandatoryRatingsPopup__header">
            <p className="studentMandatoryRatingsPopup__progress">
               Evaluare {currentIndex} din {totalCount}
            </p>
            <h3 className="studentMandatoryRatingsPopup__title">
               Evaluează lecția
            </h3>
            <p className="studentMandatoryRatingsPopup__subtitle">
               Pentru a continua, acordă un rating instructorului pentru
               lecțiile anterioare.
            </p>
         </header>

         {loading ? (
            <p className="studentMandatoryRatingsPopup__state">Se încarcă...</p>
         ) : !currentLesson ? (
            <p className="studentMandatoryRatingsPopup__state">
               Nu mai ai lecții de evaluat.
            </p>
         ) : (
            <div className="studentMandatoryRatingsPopup__content">
               <div className="studentMandatoryRatingsPopup__list">
                  <div className="studentMandatoryRatingsPopup__row">
                     <span>Instructor</span>
                     <strong>{getInstructorName(currentLesson)}</strong>
                  </div>
                  <div className="studentMandatoryRatingsPopup__row">
                     <span>Data lecției</span>
                     <strong>
                        {formatFloatingDateTime(getLessonStartTime(currentLesson))}
                     </strong>
                  </div>
               </div>

               <form
                  className="studentMandatoryRatingsPopup__form"
                  onSubmit={handleSubmit}
               >
                  <StudentRatingPicker
                     value={rating}
                     previewValue={hoverRating}
                     disabled={saving}
                     onChange={setRating}
                     onPreviewChange={setHoverRating}
                  />

                  <label className="studentMandatoryRatingsPopup__commentField">
                     <span className="studentMandatoryRatingsPopup__fieldLabel">
                        Comentariu{" "}
                        {rating >= 1 && rating <= 3
                           ? "(obligatoriu)"
                           : "(opțional)"}
                     </span>
                     <textarea
                        className="studentMandatoryRatingsPopup__commentInput"
                        value={comment}
                        onChange={(event) => setComment(event.target.value)}
                        placeholder={
                           rating >= 1 && rating <= 3
                              ? "Spune-ne ce nu a mers bine la lecție..."
                              : "Spune-ne cum a fost lecția..."
                        }
                        rows={4}
                        disabled={saving}
                        required={rating >= 1 && rating <= 3}
                     />
                  </label>

                  {error && (
                     <p className="studentMandatoryRatingsPopup__error">{error}</p>
                  )}

                  <div className="studentAddReservation__add-btns studentAddReservation__add-btns--single">
                     <button
                        type="submit"
                        className="studentAddReservation__add-btn studentAddReservation__add-btn--arrow studentAddReservation__add-btn--full"
                        disabled={saving || !rating || (rating >= 1 && rating <= 3 && !comment.trim())}
                     >
                        <span>
                           {saving ? "Se trimite..." : "Trimite ratingul"}
                        </span>
                        <ReactSVG
                           src={arrowIcon}
                           className="studentAddReservation__add-btn-icon"
                        />
                     </button>
                  </div>
               </form>
            </div>
         )}
      </div>
   );
}
